import { supabase } from './supabase';
import { makeHeaderMatcher, textCell, boolCell } from './importCore';
import { generateSkuForCategory, nextAvailableSku } from './skuGenerator';
import { addUnit } from './taxonomy';
import { CREATE_UNIT } from './vendorItemsImport';

// Issue #422, item half. Session 78t. Gate decisions M13-M15, M22, and S14 of
// the SKU gate (#471).
//
// IN PLAIN TERMS: this is the half of the importer that loads the things D&R
// actually stocks. A row is one item in the Inventory catalog - its name, what
// one of it IS (a sheet, a foot, an each), which category it files under, and
// optionally who we usually buy it from. The other importer loads a supplier's
// price list; this one decides what is in the catalog at all, which is why it
// is gated to admins, managers and executives and the other is not.
//
// THIS FILE IS THE FIRST REAL TEST OF M22's CLAIM. importDescriptors.js says
// outright: "a fourth import type should be a new object in this file and NO
// edit to the flow. If it is not, the split was drawn wrong. The item-catalog
// descriptor lands at Session 78t and is the first real use of that claim."
// It held. MasterDataImportSheet.js is not edited by this work.
//
// THREE THINGS ARE GENUINELY DIFFERENT HERE, and each is a decision rather
// than an accident:
//
//   1. BLANK SKU MEANS GENERATE, NOT EMPTY (S14). Every other importer treats
//      a blank optional cell as "leave it alone". Here it is an instruction.
//      The code is built with generateSkuForCategory() - the SAME function the
//      Add-item form and the promotion screen call - so an imported item and a
//      hand-added one cannot drift apart. That is the whole reason #471 ran
//      before this session.
//
//   2. A NEW CATEGORY MAY NOT BE CREATED BY AN IMPORT (M8), though a new
//      stocking unit may. importDescriptors.js predicted this exact split:
//      "a new PACKAGE TYPE may be added during an import; a new CATEGORY may
//      not ... which is why the item descriptor at 78t will answer this
//      differently for the same question shape." A unit is an unblock -
//      Colby's own 78n2 rule that Jennifer must not sit mid-order waiting for
//      one. A category is a decision about the SHAPE of the catalog and is
//      made in Company Settings, deliberately, by somebody looking at the
//      whole list.
//
//   3. CREATING AN ITEM CREATES ITS STOCK ROWS. An item with no
//      inventory_stock row at a branch is invisible to that branch's stock
//      screens and, per #238, a receive against it writes history and never
//      creates the row. So the write is item-plus-stock or it is a bug in
//      waiting. One row per branch at quantity zero; qty_on_hand defaults to 0
//      in the database and is deliberately not sent, because the database owns
//      that column and the app owns cost only (#240).

// ── What the file's columns might be called ──────────────────────────────────
//
// Order inside each list is the RANK: when two columns both look like the same
// field, the earlier alias wins. That is what stops a sheet carrying both
// "Item name" and "Name" from having the wrong one take the field.
//
// 'description' is deliberately NOT an alias for `name`. A supplier sheet
// routinely carries both, and guessing wrong there silently files every item
// under its blurb.
const ALIASES = {
  id: ['stede id', 'id', 'item id', 'stede item id', 'stede itemid'],
  name: ['item name', 'name', 'item', 'product name', 'material name', 'part name'],
  sku: ['sku', 'stede sku', 'item sku', 'item code', 'stock code', 'part number', 'part no'],
  category_id: ['category', 'item category', 'category name', 'subcategory'],
  uom: ['stocking unit', 'uom', 'unit', 'unit of measure', 'stock unit', 'base unit'],
  description: ['description', 'item description', 'long description', 'notes'],
  vendor_id: ['primary vendor', 'vendor', 'vendor name', 'supplier', 'supplier name', 'preferred vendor'],
  is_active: ['active', 'is active', 'enabled', 'status'],
};

// Round-tripping a Stede export: the export writes a provenance block above the
// header, so the header row is found by content rather than by counting lines.
const matcher = makeHeaderMatcher(ALIASES, { signature: ['stede id', 'stocking unit'] });

export const findHeaderRow = matcher.findHeaderRow;
export function mapHeaders(headerCells) { return matcher.mapHeaders(headerCells); }

// Name, category and unit. SKU is NOT required - S14 - and that is the single
// most important line in this file to get right, because the published guide
// already promises it.
export const REQUIRED_KEYS = ['name', 'category_id', 'uom'];

const LABEL = {
  category_id: 'Category',
  uom: 'Stocking unit',
  vendor_id: 'Primary vendor',
};

function norm(v) {
  return String(v ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

// ── Names that are not unique ────────────────────────────────────────────────
//
// Session 78u, and this fixes a defect that shipped at 78t.
//
// IN PLAIN TERMS: two subcategories can have the same name. D&R has two called
// "Cast" - one under Cast Vinyl, one under Cast Film - and nothing in the
// database forbids it: `inventory_categories` is unique on its SKU `code` and
// on nothing else. `inventory_vendors` is unique on nothing but its id.
//
// This file used to build `new Map(categories.map(c => [norm(c.name), c]))`.
// A Map keeps the LAST entry for a repeated key, so every imported row saying
// "Cast" was filed under whichever Cast happened to load last - silently, with
// no question and no warning, because as far as the importer was concerned the
// name was recognised. The item then also got the wrong SKU, since the code is
// built from the category's own code.
//
// The rule now: a lookup only ever holds a key that resolves to EXACTLY ONE
// record. Anything else is reported, never guessed.
//
//   Categories  the parent tells them apart, and it means something to a
//               person, so an ambiguous bare name becomes a QUESTION and the
//               answer is the "Cast (Cast Vinyl)" form. That is also what the
//               downloaded workbook puts in its drop-down.
//   Vendors     nothing tells two identical vendor names apart that would help
//               anybody choose, and two rows for one company is a data fault
//               rather than a real distinction. So the row is REFUSED with a
//               message saying to merge or rename them in Stede.
//
// Stocking units need none of this: `vendor_items.pack_unit` carries a foreign
// key into `units_of_measure(name)`, and a foreign key requires a unique
// target, so the database already guarantees what this code has to compute for
// the other two.

export function categoryDisplayName(cat, categories = []) {
  if (!cat) return '';
  const parent = cat.parent_id
    ? categories.find(c => String(c.id) === String(cat.parent_id))
    : null;
  return parent ? `${cat.name} (${parent.name})` : cat.name;
}

// `byName` maps a normalised value to exactly one category. `ambiguous` holds
// the bare names that resolve to more than one. `choices` is what the question
// step offers, and every one of its values is a key in `byName` by
// construction - so an answer picked from the list can never fail to resolve.
export function indexCategories(categories = []) {
  const bareCount = new Map();
  const labelCount = new Map();
  categories.forEach(c => {
    bareCount.set(norm(c.name), (bareCount.get(norm(c.name)) || 0) + 1);
    const l = norm(categoryDisplayName(c, categories));
    labelCount.set(l, (labelCount.get(l) || 0) + 1);
  });

  const byName = new Map();
  const ambiguous = new Set();
  const choices = [];

  categories.forEach(c => {
    const bare = norm(c.name);
    if (bareCount.get(bare) === 1) {
      byName.set(bare, c);
      choices.push({ value: c.name, label: c.name, id: c.id });
      return;
    }
    ambiguous.add(bare);
    // Where even the parent does not separate them - two children of one
    // parent sharing a name, or two top-level ones - nothing a person can type
    // into a spreadsheet will either. It stays unresolvable and the row is
    // refused rather than guessed at.
    const display = categoryDisplayName(c, categories);
    if (labelCount.get(norm(display)) === 1) {
      byName.set(norm(display), c);
      choices.push({ value: display, label: display, id: c.id });
    }
  });

  return { byName, ambiguous, choices };
}

export function indexVendors(vendors = []) {
  const count = new Map();
  vendors.forEach(v => count.set(norm(v.name), (count.get(norm(v.name)) || 0) + 1));

  const byName = new Map();
  const ambiguous = new Set();
  const choices = [];
  vendors.forEach(v => {
    const k = norm(v.name);
    if (count.get(k) === 1) {
      byName.set(k, v);
      choices.push({ value: v.name, label: v.name, id: v.id });
    } else {
      ambiguous.add(k);
    }
  });

  return { byName, ambiguous, choices };
}

// `mapping` is INDEX -> KEY, not the other way round. Reading it backwards
// yields an empty row that reports "this row has no item name" for a file whose
// name column is right there, which is what it did before this comment existed.
function readRow(cells, mapping) {
  const supplied = {};
  for (const [indexStr, key] of Object.entries(mapping || {})) {
    const raw = cells[Number(indexStr)];
    if (raw === undefined) continue;
    supplied[key] = raw;
  }
  return { supplied };
}

function isBlankRow(cells) {
  return cells.every(c => String(c ?? '').trim() === '');
}

// ── The questions ────────────────────────────────────────────────────────────
//
// #468 layers 1 and 2, pointed at the item sheet. An unrecognised value becomes
// ONE question that fixes every row using it, rather than a rejection per row
// (M7). The lists are read at import time, so unlike a drop-down baked into a
// downloaded file they cannot go stale (M6).
//
// THREE fields ask questions here where the vendor sheet asked about one, and
// that exposed a real limit in the shared flow which is handled here rather
// than by editing it. MasterDataImportSheet.js stores answers in a single map
// keyed by the VALUE ALONE - `valueMap[q.value.trim().toLowerCase()]` - so two
// questions carrying the same text in different columns would share one answer.
// With one asking field that could never happen; with three it can. When the
// same unknown text appears in more than one column, the displayed value is
// disambiguated ("Vinyl (category)") so the keys differ, and `raw` carries the
// original text for matching rows. Files that do not collide - which is nearly
// all of them - read exactly as before.
export function collectUnknownValues({ rows, headerRowIndex, mapping, categories, units, vendors }) {
  // Session 78u. "Known" means RESOLVABLE TO ONE RECORD, not merely present.
  //
  // Categories: an ambiguous bare name is deliberately NOT known, so "Cast"
  // becomes a question and the person picks "Cast (Cast Vinyl)". That reuses
  // M7's machinery exactly rather than inventing a second mechanism.
  //
  // Vendors: a duplicated name IS treated as known here, on purpose, so that no
  // question is raised - the question step would offer two identical options
  // and nobody could tell them apart. buildItemsPlan refuses the row instead,
  // naming the real fix, which is to merge or rename them in Stede.
  const known = {
    category_id: new Set(indexCategories(categories || []).byName.keys()),
    uom: new Set((units || []).map(u => norm(u.name))),
    vendor_id: new Set((vendors || []).map(v => norm(v.name))),
  };

  const found = new Map(); // `${field}::${normValue}` -> question

  for (let r = headerRowIndex + 1; r < rows.length; r += 1) {
    const cells = rows[r];
    if (isBlankRow(cells)) continue;
    const { supplied } = readRow(cells, mapping);

    for (const field of ['category_id', 'uom', 'vendor_id']) {
      const raw = supplied[field];
      if (raw === undefined) continue;
      const text = String(raw).trim();
      if (!text) continue;                    // blank is not unknown, it is absent
      if (known[field].has(norm(text))) continue;

      const key = `${field}::${norm(text)}`;
      if (!found.has(key)) {
        found.set(key, { field, label: LABEL[field], raw: text, value: text, lines: [] });
      }
      found.get(key).lines.push(r + 1);
    }
  }

  const questions = [...found.values()];

  // Disambiguate only where the same text is genuinely asked about twice.
  const byText = new Map();
  questions.forEach(q => {
    const k = norm(q.raw);
    byText.set(k, (byText.get(k) || 0) + 1);
  });
  questions.forEach(q => {
    if (byText.get(norm(q.raw)) > 1) q.value = `${q.raw} (${q.label.toLowerCase()})`;
  });

  return questions.map(q => ({ ...q, count: q.lines.length }));
}

// The answer for a supplied value.
//
// THIS RESOLVES FROM `valueMap` ALONE, AND MUST. MasterDataImportSheet.js calls
// buildPlan with `{ rows, headerRowIndex, mapping, reference, valueMap }` and
// does NOT pass the questions. An earlier version looked the question up first
// and returned undefined without one, so on the real screen every answer was
// silently ignored and a category the person had just mapped still reported
// "Stede has no category called ...".
//
// The unit tests did not catch it because the test helper passed `questions`
// explicitly - a fixture supplying something the screen does not. That is
// Session 78s.3's lesson exactly, and the test now mirrors the screen instead.
//
// Two lookups, because collectUnknownValues disambiguates a value asked about
// by more than one column: the plain key first, then the "(category)" form.
// Only one of the two can exist for any given value, since disambiguation is
// applied to every question sharing that text or to none of them.
function answerFor(field, text, valueMap) {
  const map = valueMap || {};
  const plain = map[String(text).trim().toLowerCase()];
  if (plain !== undefined) return plain;
  const label = (LABEL[field] || '').toLowerCase();
  return map[`${String(text).trim()} (${label})`.toLowerCase()];
}

export function unitsToCreate(questions, valueMap) {
  return (questions || [])
    .filter(q => q.field === 'uom' && (valueMap || {})[q.value.trim().toLowerCase()] === CREATE_UNIT)
    .map(q => q.raw);
}

// ── The plan ─────────────────────────────────────────────────────────────────
//
// Everything the preview shows, computed before a single write. Nothing here
// queries: the reference lists are read once by the screen and passed in, so
// this function is pure and every rule below is testable with no database.
export function buildItemsPlan({
  rows, headerRowIndex, mapping,
  items = [], categories = [], units = [], vendors = [], branches = [],
  valueMap = {},
}) {
  const plan = [];

  const byId = new Map(items.map(i => [String(i.id), i]));
  const bySku = new Map(items.filter(i => i.sku).map(i => [norm(i.sku), i]));
  // Session 78u. These were plain Maps keyed on the bare name, and a Map keeps
  // the LAST entry for a repeated key - so a duplicated category or vendor name
  // silently resolved to whichever loaded last. See the block above `norm`.
  const { byName: catByName, ambiguous: catAmbiguous } = indexCategories(categories);
  const { byName: vendorByName, ambiguous: vendorAmbiguous } = indexVendors(vendors);
  const unitByName = new Map(units.map(u => [norm(u.name), u]));

  // Every SKU that will exist when this import finishes: what is stored now,
  // plus every code this file claims or generates. Without the second half two
  // rows generating the same base code would both pass the preview and the
  // second would be refused by the unique index at write time - a clean preview
  // and a failed import, which is the worst pairing available.
  const takenSkus = new Set(items.filter(i => i.sku).map(i => norm(i.sku)));
  const skuClaimedBy = new Map(); // norm(sku) -> lineNo, for same-file collisions

  const seenIds = new Map();

  for (let r = headerRowIndex + 1; r < rows.length; r += 1) {
    const cells = rows[r];
    if (isBlankRow(cells)) continue;

    const lineNo = r + 1;
    const entry = { lineNo, errors: [], warnings: [], changes: [], values: {} };
    const { supplied } = readRow(cells, mapping);

    const name = textCell(supplied.name);
    const suppliedSku = textCell(supplied.sku);
    const rawCategory = textCell(supplied.category_id);
    const rawUnit = textCell(supplied.uom);
    const rawVendor = textCell(supplied.vendor_id);
    // D3: a blank cell means "leave this alone" and NEVER clears a stored
    // value, which is why textCell returns undefined rather than ''. Writing
    // `description || null` here would wipe an item's description every time
    // somebody imported a sheet with that column empty.
    const description = textCell(supplied.description);
    const idText = textCell(supplied.id);

    entry.displayName = name || suppliedSku || `Row ${lineNo}`;

    // ── Which record is this row about ──────────────────────────────────────
    //
    // 'Stede ID' carries the template's stated contract: blank creates, an
    // existing id updates, an id that does not exist is REJECTED. Nobody looks
    // up the next number; the column exists so that editing an export works.
    let target = null;
    if (idText) {
      if (!/^\d+$/.test(idText)) {
        entry.errors.push(`"${idText}" is not a Stede ID. Leave the column blank to add a new item, or paste the ID from an export to change one.`);
      } else if (!byId.has(idText)) {
        entry.errors.push(`No item in Stede has ID ${idText}. Leave the column blank to add it as a new item instead.`);
      } else {
        target = byId.get(idText);
        const twin = seenIds.get(idText);
        if (twin) entry.errors.push(`Row ${twin} is about the same item. Only one row per item.`);
        seenIds.set(idText, lineNo);
      }
    } else if (suppliedSku && bySku.has(norm(suppliedSku))) {
      // A natural-key match. The template says blank ID creates, but creating
      // an item whose SKU already exists is refused by inventory_items_sku_key
      // every single time, so treating it as a create guarantees a failed row.
      // Matching it is what the person meant, and the warning says so out loud
      // rather than doing it silently.
      target = bySku.get(norm(suppliedSku));
      entry.warnings.push(`Matched to the item that already has SKU ${suppliedSku}, so this row changes it rather than adding a second one.`);
    }

    if (!name) {
      entry.errors.push('This row has no item name, so there is nothing to add or change.');
    }

    // ── Category: required, and never created by an import (M8) ─────────────
    let category = null;
    if (!rawCategory) {
      entry.errors.push('This row has no category. Every item files under one, and an import cannot make a new category — add it in Company Settings first.');
    } else {
      const answered = answerFor('category_id', rawCategory, valueMap);
      const effective = answered === undefined ? rawCategory : answered;
      if (effective === '' ) {
        entry.errors.push(`"${rawCategory}" was left unmatched, and an item cannot be filed without a category.`);
      } else if (catByName.has(norm(effective))) {
        category = catByName.get(norm(effective));
        entry.values.category_id = category.id;
      } else if (catAmbiguous.has(norm(effective))) {
        // Session 78u. Reached when the value was never put through the question
        // step, or when the parent does not separate them either. Naming the
        // options is the whole point - the old code picked one at random.
        const options = categories
          .filter(c => norm(c.name) === norm(effective))
          .map(c => `"${categoryDisplayName(c, categories)}"`)
          .join(' or ');
        entry.errors.push(`Stede has more than one category called "${rawCategory}" - ${options}. Say which one you mean by writing it that way in the file, or choose it on the previous step.`);
      } else {
        entry.errors.push(`Stede has no category called "${rawCategory}". Categories are added in Company Settings, deliberately - an import never creates one.`);
      }
    }

    // ── Stocking unit: required; a new one MAY be created (M8) ──────────────
    if (!rawUnit) {
      entry.errors.push('This row has no stocking unit, so Stede would not know what one of it is.');
    } else {
      const answered = answerFor('uom', rawUnit, valueMap);
      const effective = answered === undefined ? rawUnit : answered;
      if (effective === CREATE_UNIT) {
        entry.values.uom = rawUnit;      // created before any row is written
      } else if (effective === '') {
        entry.errors.push(`"${rawUnit}" was left unmatched, and an item cannot be stored without a stocking unit.`);
      } else if (!unitByName.has(norm(effective))) {
        entry.errors.push(`Stede has no stocking unit called "${rawUnit}".`);
      } else {
        entry.values.uom = unitByName.get(norm(effective)).name;
      }
    }

    // ── Primary vendor: optional, but a name that matches nothing is a
    //    refusal rather than a silent blank. M4's reasoning on the vendor
    //    sheet applies unchanged: a typo must not quietly become an orphan.
    if (rawVendor) {
      const answered = answerFor('vendor_id', rawVendor, valueMap);
      const effective = answered === undefined ? rawVendor : answered;
      if (effective === '') {
        entry.values.vendor_id = null;
        entry.warnings.push(`No primary vendor set for this row - "${rawVendor}" was left unmatched.`);
      } else if (vendorByName.has(norm(effective))) {
        entry.values.vendor_id = vendorByName.get(norm(effective)).id;
      } else if (vendorAmbiguous.has(norm(effective))) {
        // Session 78u. Two vendor rows for one company is a data fault, and
        // nothing a person could type would separate them, so this refuses the
        // row and names the real fix rather than picking one.
        entry.errors.push(`Stede has more than one vendor called "${rawVendor}", so this row cannot say which is meant. Merge or rename them in Stede, then import again.`);
      } else {
        entry.errors.push(`Stede has no vendor called "${rawVendor}". Import the vendor list first, or clear the column to leave the primary vendor unset.`);
      }
    }

    // boolCell returns undefined for blank and an OBJECT carrying `.error` for
    // a word it cannot read - never null. Testing for null let every
    // unparseable value through as an object and wrote it to the column.
    const activeCell = boolCell(supplied.is_active);
    if (activeCell && typeof activeCell === 'object') {
      entry.warnings.push(`${activeCell.error} Active was left as it is.`);
    } else if (activeCell !== undefined) {
      entry.values.is_active = activeCell;
    }

    if (name) entry.values.name = name;
    if (description !== undefined) entry.values.description = description;

    // ── The SKU. S14: blank MEANS GENERATE. ────────────────────────────────
    if (entry.errors.length === 0) {
      if (suppliedSku) {
        const clash = bySku.get(norm(suppliedSku));
        if (clash && (!target || String(clash.id) !== String(target.id))) {
          entry.errors.push(`SKU ${suppliedSku} already belongs to "${clash.name}". Two items cannot share a code.`);
        } else if (skuClaimedBy.has(norm(suppliedSku))) {
          entry.errors.push(`Row ${skuClaimedBy.get(norm(suppliedSku))} already uses SKU ${suppliedSku} in this file.`);
        } else {
          entry.values.sku = suppliedSku;
          entry.skuSource = 'file';
          skuClaimedBy.set(norm(suppliedSku), lineNo);
          takenSkus.add(norm(suppliedSku));
        }
      } else if (target && target.sku) {
        // S8: an existing code is frozen. A blank cell on a row that already
        // has a SKU means "unchanged", never "make me a new one".
        entry.skuSource = 'existing';
      } else if (category) {
        const base = generateSkuForCategory({ categories, categoryId: category.id, name });
        if (!base) {
          entry.errors.push(`Stede could not build a SKU for this row, because the category "${category.name}" has no code yet. Add one in Company Settings, or type a SKU into the file.`);
        } else {
          const code = nextAvailableSku(base, [...takenSkus]);
          entry.values.sku = code;
          entry.skuSource = 'generated';
          skuClaimedBy.set(norm(code), lineNo);
          takenSkus.add(norm(code));
        }
      }
    }

    if (entry.errors.length) {
      entry.mode = 'error';
      plan.push(entry);
      continue;
    }

    if (target) {
      entry.rowId = target.id;
      entry.displayName = name || target.name;
      const LABELS = {
        name: 'name', sku: 'SKU', category_id: 'category', uom: 'stocking unit',
        description: 'description', vendor_id: 'primary vendor', is_active: 'active',
      };
      Object.entries(entry.values).forEach(([key, after]) => {
        const before = target[key];
        const same = (before ?? null) === (after ?? null)
          || String(before ?? '') === String(after ?? '');
        if (!same) entry.changes.push({ key, label: LABELS[key] || key, before, after });
      });
      entry.mode = entry.changes.length ? 'update' : 'unchanged';
    } else {
      entry.mode = 'create';
      entry.branchCount = branches.length;
      if (entry.values.is_active === undefined) entry.values.is_active = true;
    }

    plan.push(entry);
  }

  return plan;
}

// ── The write ────────────────────────────────────────────────────────────────
//
// Row by row rather than as one transaction, for the same reason the other two
// importers are: a partial import is the INTENDED behaviour, so a failure part
// way through leaves the rows that succeeded in place and reports the rest.
// Locked rule 7 governs multi-table writes that must succeed TOGETHER - and the
// item-plus-stock pair below IS such a write, which is why it is undone by hand
// when the stock rows fail rather than being left half-made.
//
// NEW UNITS ARE CREATED FIRST, ONCE, BEFORE ANY ROW, because inventory_items.uom
// is a foreign key into units_of_measure and a row naming a unit that does not
// exist yet is refused by the database. Creating them here rather than when the
// person answers the question means abandoning the preview creates nothing.
//
// #450's audit trigger fires per row and records who and when. Because this
// runs through the app as a signed-in user, the null auth.uid() that breaks a
// bulk load in the SQL editor cannot happen here - which is #173, and the
// reason the importer belongs in the app rather than in a script.
export async function executeItemsPlan(plan, { newUnits = [], branches = [] } = {}, onProgress) {
  const result = {
    created: 0, updated: 0, unchanged: 0, skipped: 0,
    unitsAdded: 0, stockRows: 0, failed: [],
  };
  const writable = plan.filter(e => e.mode === 'create' || e.mode === 'update');

  result.unchanged = plan.filter(e => e.mode === 'unchanged').length;
  result.skipped = plan.filter(e => e.mode === 'error').length;

  for (const name of newUnits) {
    // 'measure' rather than 'both': this column answers "what IS one of these",
    // and a stocking word that is also a packaging word can be widened in
    // Company Settings by somebody who has looked at both lists. Guessing
    // 'both' here would push a stocking word into the vendor pack-unit picker
    // on every pricing screen. The mirror of the vendor importer's own choice.
    const { error } = await addUnit(name, 'measure', null);
    if (error) {
      return {
        ...result,
        fatal: `Could not add the stocking unit “${name}”: ${error.message || error}. Nothing was imported.`,
      };
    }
    result.unitsAdded += 1;
  }

  let done = 0;
  for (const entry of writable) {
    try {
      if (entry.mode === 'create') {
        const { data, error } = await supabase
          .from('inventory_items').insert(entry.values).select('id').single();
        if (error) throw error;
        result.created += 1;

        // #238. An item with no stock row at a branch is invisible to that
        // branch and a receive against it writes history without creating the
        // row. qty_on_hand is deliberately not sent: it defaults to 0, and the
        // database owns that column (#240).
        if (branches.length) {
          const { error: stockErr } = await supabase.from('inventory_stock').insert(
            branches.map(b => ({ item_id: data.id, branch_id: b.id })),
          );
          if (stockErr) {
            // The pair must not be left half-made. Undo the item so the row can
            // be fixed and re-imported, rather than leaving a catalog entry no
            // branch can see.
            await supabase.from('inventory_items').delete().eq('id', data.id);
            result.created -= 1;
            throw new Error(`the item was added but its stock rows were refused (${stockErr.message}), so it was removed again`);
          }
          result.stockRows += branches.length;
        }
      } else {
        const patch = Object.fromEntries(entry.changes.map(c => [c.key, c.after]));
        const { error } = await supabase
          .from('inventory_items').update(patch).eq('id', entry.rowId);
        if (error) throw error;
        result.updated += 1;
      }
    } catch (e) {
      result.failed.push({ lineNo: entry.lineNo, name: entry.displayName, message: e.message || String(e) });
    }
    done += 1;
    if (onProgress) onProgress(done, writable.length);
  }

  return result;
}
